import { useCallback } from 'react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';

import { DEFAULT_KNOWLEDGE_SUBTAB, KNOWLEDGE_SUBTABS } from '../../constants/knowledge.js';
import { useTabPath } from '../../hooks/useTabPath.js';
import { useTruthSummary } from '../../hooks/useTruthSummary.js';
import RoutedSubtabSwitch from '../RoutedSubtabSwitch.jsx';
import KnowledgeSummaryLine from './KnowledgeSummaryLine.jsx';

/**
 * The shell of Knowledge (CT-5717), shared by Drive, Sandbox and Truth: the
 * subtab switch and the summary line, above whichever subtab the route names.
 * The profile's context is passed through.
 *
 * The subtab belongs to its RECORD: the tab stays mounted when the user moves to
 * another one, so the subtab is remounted (`key`) — an open panel, a request that
 * answers late, a page or a chip never carries over to the next record.
 */
function KnowledgeLayout() {
  const { t } = useTranslation('unified-profiles');
  const profileContext = useOutletContext();
  const { entityType, entityId } = profileContext;
  const recordKey = `${entityType}/${entityId}`;
  const { summary } = useTruthSummary({ entityType, entityId });

  const navigate = useNavigate();
  const tabPath = useTabPath();
  const viewTruth = useCallback(() => navigate(`${tabPath}/truth`), [navigate, tabPath]);
  // "View in Truth" leads nowhere on Truth itself (CT-5719).
  const splat = useParams()['*'] ?? '';
  const onTruth = splat.split('/')[0] === 'truth';

  return (
    <div data-testid="knowledge-tab" className="flex min-w-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <RoutedSubtabSwitch
          subtabs={KNOWLEDGE_SUBTABS}
          defaultSubtab={DEFAULT_KNOWLEDGE_SUBTAB}
          labelPrefix="knowledge.subtabs"
          ariaLabel={t('knowledge.subtabs.ariaLabel')}
          widthClassName="w-[260px]"
        />
        <KnowledgeSummaryLine summary={summary} onViewTruth={onTruth ? undefined : viewTruth} />
      </div>
      <Outlet key={recordKey} context={profileContext} />
    </div>
  );
}

export default KnowledgeLayout;
